// src/components/PublicHeader.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, User, Settings, LogOut,
  Globe, AlertTriangle
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import ThemeToggle from "./common/ThemeToggle";

const PublicHeader = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const { user, logout } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleProfileClick = () => {
    setIsUserMenuOpen(false);
    navigate("/profile");
  };

  const handleNavigationClick = () => {
    setIsUserMenuOpen(false);
    const site = user?.tenant || 'AXON';
    navigate(`/navigate/${site}`);
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    navigate('/');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4 shadow-sm h-16 transition-all duration-300 ${isScrolled
      ? 'bg-white/95 backdrop-blur-xl shadow-lg'
      : 'bg-white/80 backdrop-blur-lg'
      }`}>
      {/* Left Logo */}
      <img src="/Axon-logo.png" alt="Axon Logo" className="h-10 cursor-pointer" onClick={() => navigate('/')} />

      {/* Right Section */}
      <div className="flex items-center space-x-4">
        {/* Right Logo */}
        <img src="/secondary-logo.png" alt="Secondary Logo" className="h-16" />

        {/* Theme Toggle */}
        <ThemeToggle size="md" />

        {/* User Menu - Only show if user is logged in */}
        {user && (
          <div className="relative" ref={userMenuRef}>
            <motion.button
              onClick={handleUserMenuToggle}
              className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 hover:scale-105 text-gray-700 hover:bg-gray-100"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="text-left">
                <span className="public-header-username text-sm font-medium text-[#16202B]">{user.username}</span>
                <div className="public-header-fullname text-xs text-gray-700">
                  {user.fullName || user.username}
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''} text-gray-400`} />
            </motion.button>

            <AnimatePresence>
              {isUserMenuOpen && (
                <motion.div
                  className="absolute right-0 top-full mt-2 w-64 rounded-xl shadow-2xl z-[60] border bg-white/95 backdrop-blur-xl border-gray-200/50"
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  {/* Dropdown Arrow */}
                  <div className="absolute -top-2 right-4 w-4 h-4 rotate-45 bg-white border-l border-t border-gray-200"></div>

                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-gray-200/50">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="public-header-menu-name text-sm text-gray-900">
                          {user.fullName || user.username}
                        </p>
                        <p className="public-header-menu-subtitle text-xs text-gray-600">
                          {user.username} • {user.role || 'User'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="px-2 py-2">
                    <div className="public-header-section-label px-2 py-1 text-[11px] font-semibold text-[#6B7A8B] uppercase tracking-[0.1em]">
                      Quick Actions
                    </div>

                    <button
                      onClick={handleNavigationClick}
                      className="public-header-menu-item w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 text-gray-800 hover:bg-gray-100 hover:text-blue-600"
                    >
                      <Globe className="w-4 h-4 mr-3" />
                      Homepage
                    </button>
                  </div>

                  {/* Divider */}
                  <div className="mx-2 h-px bg-gray-200/50"></div>

                  {/* Account & Settings */}
                  <div className="px-2 py-2">
                    <div className="public-header-section-label px-2 py-1 text-[11px] font-semibold text-[#6B7A8B] uppercase tracking-[0.1em]">
                      Account & Settings
                    </div>

                    <button
                      onClick={handleProfileClick}
                      className="public-header-menu-item w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 text-gray-800 hover:bg-gray-100 hover:text-blue-600"
                    >
                      <Settings className="w-4 h-4 mr-3" />
                      Profile Settings
                    </button>


                  </div>

                  {/* Divider */}
                  <div className="mx-2 h-px bg-gray-200/50"></div>

                  {/* Logout */}
                  <div className="px-2 py-2">
                    <button
                      onClick={handleLogout}
                      className="public-header-logout w-full flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <LogOut className="w-4 h-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;
